/**
 * Resolve a theme CSS variable to a REAL color string for JS contexts that
 * cannot parse `var()` — canvas (fillStyle/strokeStyle), chart.js / TradingView
 * option objects, and SVG presentation attributes set via JSX props.
 *
 * CSS contexts read vars directly as `var(--x)`; this helper reads the SAME vars
 * via getComputedStyle so JS-drawn surfaces follow them too. Used for the
 * direction (up/down) colors (`--color-up` / `--color-down`, written by
 * applyDirectionColors) on the candle chart.
 *
 * Values may be an RGB space-triplet ("22 199 132") — pass `fallback` as a
 * triplet when you need alpha — or a ready color string. Falls back to
 * `fallback` when the var is unset (SSR / no DOM).
 */
export function cssColor(varName: string, fallback: string, alpha?: number): string {
  let raw = "";
  if (typeof window !== "undefined" && typeof document !== "undefined") {
    raw = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  }
  const value = raw || fallback;

  // RGB space-triplet (e.g. "22 199 132") → wrap in rgb()/rgba(), honoring alpha.
  // Emit COMMA-separated form: the modern space/slash syntax (`rgb(a b c / x)`)
  // is valid CSS but is rejected by JS color parsers used on canvas surfaces
  // (e.g. lightweight-charts' colorStringToRgba), which only accept
  // `rgb(a, b, c)` / `rgba(a, b, c, x)` or hex.
  if (/^\d{1,3}\s+\d{1,3}\s+\d{1,3}$/.test(value)) {
    const rgb = value.split(/\s+/).join(", ");
    return alpha != null && alpha !== 1 ? `rgba(${rgb}, ${alpha})` : `rgb(${rgb})`;
  }

  // Already a color string (hex / rgb / rgba) → use as-is.
  return value;
}
